import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '../common/Loader.js';
import { DashboardLayout } from '../common/page_component/index.js';
import { paymentService } from '../../services/ShipmentPaymentService.js';
import { shipmentService } from '../../services/shipmentService.js';
import Alert from '../common/Alert.js';
import { Plus, Filter, MapPin, Pencil, CreditCard, ArrowLeft } from "lucide-react";
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';
import { formatUsernameLocation } from '../../utils/userDisplay';


export default function ViewPayment() {
  const navigate = useNavigate();
  const { paymentId } = useParams();
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState(null);
  const [shipmentDetails, setShipmentDetails] = useState(null);
  const [allPayments, setAllPayments] = useState([]);

  useEffect(() => {
    fetchPaymentDetails();
  }, [paymentId]);

  const fetchPaymentDetails = async () => {
    try {
      setLoading(true);
      
      // Fetch payment details
      const paymentResponse = await paymentService.getPaymentById(paymentId);
      const paymentData = paymentResponse.data.data;
      setPayment(paymentData);

      // Fetch shipment details
      const shipmentResponse = await shipmentService.getShipment(paymentData.shipment_id);
      const shipment = shipmentResponse.data.shipment || shipmentResponse.data.data;
      setShipmentDetails(shipment);

      // Fetch all payments for this shipment
      const allPaymentsResponse = await paymentService.getPaymentHistory(paymentData.shipment_id);
      setAllPayments(allPaymentsResponse.data.data.payment_history || []);
      
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to fetch payment details');
      navigate('/payments');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = await Alert.confirm(
      `Delete payment record of ₹${payment.payment_amount?.toFixed(2)} for shipment ${payment.shipment_id}?`,
      'Delete Payment Record'
    );

    if (!confirmed) return;

    try {
      await paymentService.deletePayment(payment.id);
      Alert.success('Payment record deleted successfully');
      navigate('/payments');
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to fetch payment details');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading payment details..." />
        </div>
      </DashboardLayout>
    )
  }

  if (!payment || !shipmentDetails) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="text-6xl mb-4">🔍</div>
              <p className="text-gray-600 text-lg font-semibold">Payment not found</p>
            </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
          <div className="max-w-5xl mx-auto">
            {/* Header Section */}
            <div className="mb-6">
              <Button variant="secondary" onClick={() => navigate('/payments')} icon={ArrowLeft}>
                Back to Payment History
              </Button>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-br from-green-500 to-green-600 p-3 rounded-xl shadow-lg">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Payment Details</h1>
                    <p className="text-gray-600 text-sm mt-1">Payment ID: #{payment.id}</p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={() => navigate(`/payments/edit/${payment.id}`)} icon={Pencil}>
                    Edit
                  </Button>
                  <Button variant="danger" onClick={handleDelete}>
                    Delete
                  </Button>
                </div>
              </div>
            </div>

            {/* Payment Information Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-6">
              <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                  Payment Information
                </h2>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-6">
                  {/* Payment Amount */}
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm font-bold text-green-700 uppercase tracking-wide">Payment Amount</p>
                    </div>
                    <p className="text-3xl font-bold text-green-700">₹{payment.payment_amount?.toFixed(2)}</p>
                  </div>

                  {/* Payment Mode */}
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4 border border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      <p className="text-sm font-bold text-blue-700 uppercase tracking-wide">Payment Mode</p>
                    </div>
                    <span className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg text-lg font-bold uppercase">
                      {payment.payment_mode?.replace(/_/g, ' ')}
                    </span>
                  </div>

                  {/* Previous Balance */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <p className="text-sm font-medium text-gray-600 uppercase tracking-wide mb-2">Previous Balance</p>
                    <p className="text-2xl font-bold text-gray-900">₹{payment.previous_balance?.toFixed(2)}</p>
                  </div>

                  {/* New Balance */}
                  <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-4 border border-red-200">
                    <div className="flex items-center gap-2 mb-2">
                      <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm font-bold text-red-700 uppercase tracking-wide">New Balance</p>
                    </div>
                    <p className="text-2xl font-bold text-red-700">₹{payment.new_balance?.toFixed(2)}</p>
                  </div>

                  {/* Payment Date */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <p className="text-sm font-medium text-gray-600 uppercase tracking-wide mb-2">Payment Date</p>
                    <p className="text-lg font-bold text-gray-900">
                      {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : 'N/A'}
                    </p>
                  </div>

                  {/* Recorded By */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <p className="text-sm font-medium text-gray-600 uppercase tracking-wide mb-2">Recorded By</p>
                    <p className="text-lg font-bold text-gray-900">
                      {payment.recorded_by_display
                        || formatUsernameLocation(
                          payment.recorded_by_name || payment.recorded_by_username,
                          payment.recorded_by_location,
                          payment.recorded_by_name || payment.recorded_by_user_id
                        )}
                    </p>
                  </div>
                </div>

                {/* Remarks */}
                {payment.has_receipt && payment.receipt_url && (
                  <div className="mt-6 bg-blue-50 rounded-xl p-4 border border-blue-200">
                    <p className="text-sm font-bold text-blue-800 uppercase tracking-wide mb-3">Payment Receipt</p>
                      <a
                      href={payment.receipt_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-semibold"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View Receipt
                    </a>
                  </div>
                )}

                {payment.remarks && (
                  <div className="mt-6 bg-amber-50 rounded-xl p-4 border border-amber-200">
                    <div className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <p className="text-sm font-bold text-amber-800 uppercase tracking-wide mb-1">Remarks</p>
                        <p className="text-gray-900">{payment.remarks}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Shipment Details Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-6">
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  Associated Shipment
                </h2>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-4">
                  <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
                    <p className="text-xs font-medium text-orange-700 uppercase tracking-wide mb-1">Shipment ID</p>
                    <Button variant="link" onClick={() => navigate(`/shipments/view/${shipmentDetails.shipment_id}`)} className="font-mono font-bold text-orange-600 hover:text-orange-700 text-lg hover:underline">
                      #{shipmentDetails.shipment_id}
                    </Button>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Status</p>
                    <span className="inline-block px-3 py-1 bg-blue-600 text-white rounded-lg text-sm font-bold uppercase">
                      {shipmentDetails.status}
                    </span>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Sender</p>
                    <p className="font-bold text-gray-900">{shipmentDetails.sender_name}</p>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Receiver</p>
                    <p className="font-bold text-gray-900">{shipmentDetails.receiver_name}</p>
                  </div>

                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                    <p className="text-xs font-medium text-blue-700 uppercase tracking-wide mb-1">Total Amount</p>
                    <p className="font-bold text-blue-700 text-lg">₹{shipmentDetails.total_amount?.toFixed(2)}</p>
                  </div>

                  <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <p className="text-xs font-medium text-green-700 uppercase tracking-wide mb-1">Paid Amount</p>
                    <p className="font-bold text-green-700 text-lg">₹{shipmentDetails.paid_amount?.toFixed(2)}</p>
                  </div>

                  <div className={`rounded-xl p-4 border ${
                    shipmentDetails.balance_amount === 0 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <p className={`text-xs font-medium uppercase tracking-wide mb-1 ${
                      shipmentDetails.balance_amount === 0 ? 'text-green-700' : 'text-red-700'
                    }`}>Balance Amount</p>
                    <p className={`font-bold text-lg ${
                      shipmentDetails.balance_amount === 0 ? 'text-green-700' : 'text-red-700'
                    }`}>₹{shipmentDetails.balance_amount?.toFixed(2)}</p>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Agent</p>
                    <p className="font-bold text-gray-900">{shipmentDetails.agent_name || 'N/A'}</p>
                  </div>
                </div>

                <div className="mt-4">
                  <Button variant="primary" onClick={() => navigate(`/shipments/view/${shipmentDetails.shipment_id}`)} fullWidth size="lg">
                    View Full Shipment Details
                  </Button>
                </div>
              </div>
            </div>

            {/* All Payments for This Shipment */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-4">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                    <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                  </svg>
                  Payment History for This Shipment
                </h2>
              </div>

              <div className="p-6">
                {allPayments.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-5xl mb-4">📋</div>
                    <p className="text-gray-600">No payment records found</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {allPayments.map((p) => (
                      <div
                        key={p.id}
                        className={`rounded-xl p-4 border transition-all ${
                          p.id === payment.id
                            ? 'bg-purple-50 border-purple-400 shadow-lg'
                            : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {p.id === payment.id && (
                                <span className="px-2 py-1 bg-purple-600 text-white text-xs font-bold rounded uppercase">
                                  Current
                                </span>
                              )}
                              <span className="text-sm text-gray-600">
                                {p.payment_date ? new Date(p.payment_date).toLocaleDateString('en-IN', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) : 'N/A'}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                              <div>
                                <span className="text-gray-600">Amount: </span>
                                <span className="font-bold text-green-600">₹{p.payment_amount?.toFixed(2)}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">Mode: </span>
                                <span className="font-semibold">{p.payment_mode?.replace(/_/g, ' ')}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">By: </span>
                                <span className="font-semibold">{p.recorded_by_name}</span>
                              </div>
                            </div>
                            {p.remarks && (
                              <p className="text-xs text-gray-600 mt-2 italic">"{p.remarks}"</p>
                            )}
                            {p.has_receipt && p.receipt_url && (
                                <a
                                href={p.receipt_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-1 font-medium"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                View Receipt
                              </a>
                            )}
                          </div>
                          {p.id !== payment.id && (
                            <Button variant="primary" onClick={() => navigate(`/payment-history/view/${p.id}`)}>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              View
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
    </DashboardLayout>
  );
}